import { createSlice } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { LiveChatSnippet, SnippetSharingType } from 'apps/chat/constants/LiveChatInterfaces'
import { alert } from 'common/core'
import { SnippetTypeMap } from 'common/settings/constants/LiveChat'
import { chatApi } from 'shared/api/requests/chat'
import { SnippetDoesNotExistError } from 'shared/errors/clientLayerError'
import { createAsyncAction } from 'shared/lib/redux'
import errorTrackingService from 'utils/services/errorTrackingService'

export const fetchSnippets = createAsyncAction('liveChatSnippets/fetchSnippets', async () => {
  const response = await chatApi.snippets.fetchSnippets()
  return response.data
})

export const createSnippet = createAsyncAction(
  'liveChatSnippets/createSnippet',
  async ({
    type,
    title,
    content,
    sharingType: type_title,
  }: {
    type: SnippetTypeMap.TEXT
    title: string
    content: string
    sharingType: SnippetSharingType
  }) => {
    const response = await chatApi.snippets.createSnippet({
      body: {
        type,
        title,
        content,
        type_title,
      },
    })

    alert(l.translate('Snippet created!'), 'success')
    return response.data
  },
)

export const editSnippet = createAsyncAction(
  'liveChatSnippets/editSnippet',
  async ({
    id,
    title,
    content,
    sharingType: type_title,
  }: {
    id: number
    title: string
    content: string
    sharingType: SnippetSharingType
  }) => {
    const response = await chatApi.snippets.editSnippet({
      body: {
        snippet_id: id,
        title,
        content,
        type_title,
      },
    })

    alert(l.translate('Snippet was updated!'), 'success')

    return response.data
  },
)

export const deleteSnippet = createAsyncAction(
  'liveChatSnippets/deleteSnippet',
  async (snippetId: number) => {
    const response = await chatApi.snippets.deleteSnippet({ body: { snippet_id: snippetId } })

    alert(l.translate('Snippet was deleted!'), 'success')

    return response.data
  },
)

export const transformContentSnippet = createAsyncAction(
  'liveChat/transformContentSnippet',
  async ({
    subscriber_id,
    snippet_id,
    content,
  }: {
    subscriber_id: string
    snippet_id: number
    content: string
  }) => {
    const response = await chatApi.snippets.transformContentSnippet({
      body: {
        subscriber_id,
        snippet_id,
        content,
      },
    })

    return response.data
  },
)

const snippetsSlice = createSlice({
  name: 'liveChatSnippets',
  initialState: [] as LiveChatSnippet[],
  reducers: {
    resetSnippets() {
      return []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSnippets.fulfilled, (_state, action) => {
        return action.payload.snippets
      })
      .addCase(createSnippet.fulfilled, (state, action) => {
        state.push(action.payload)
      })
      .addCase(editSnippet.fulfilled, (state, action) => {
        const index = state.findIndex(
          (snippet: LiveChatSnippet) => snippet.snippet_id === action.payload.snippet_id,
        )
        if (index !== -1) {
          state[index] = action.payload
        } else {
          errorTrackingService.logError(new SnippetDoesNotExistError(), {
            scope: 'settings',
            section: 'live_chat',
            extra: { snippetId: action.payload.snippet_id },
          })
        }
      })
      .addCase(deleteSnippet.fulfilled, (state, action) => {
        return state.filter(
          (snippet: LiveChatSnippet) => snippet.snippet_id !== action.payload.snippet_id,
        )
      })
  },
})

export const { resetSnippets } = snippetsSlice.actions
export const reducer = snippetsSlice.reducer
